import { useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  FileText,
  Network,
  Search,
  Clock,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import type {
  ConversationRetrievalItem,
  ConversationRetrievalSource,
} from "../../lib/api";
import { useAttention, useViewEmission } from "../../lib/useAttention";

/** Sources affordance under each Memso reply. Collapsed by default;
 *  expanding reveals every retrieved item with type icon, snippet,
 *  attribution chips ("via semantic + fts"), and an "Open in
 *  workspace" link. */
export function SourcesAffordance({
  items,
}: {
  items: ConversationRetrievalItem[];
}) {
  const [open, setOpen] = useState(false);
  const viewItems = useMemo(
    () =>
      items.map((item) => ({
        itemKind: item.type,
        itemId: item.id,
        context: {
          type: item.type,
          scope: item.scope,
          stores: item.sources.map((s) => s.store),
        },
      })),
    [items],
  );
  useViewEmission("recall", open ? viewItems : []);
  if (items.length === 0) return null;
  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
      >
        {open ? (
          <ChevronDown className="w-3.5 h-3.5" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5" />
        )}
        Sources
        <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200">
          {items.length}
        </span>
      </button>
      {open && (
        <ol className="mt-2 space-y-2">
          {items.map((item, idx) => (
            <SourceRow key={item.id} item={item} index={idx + 1} />
          ))}
        </ol>
      )}
    </div>
  );
}

function SourceRow({
  item,
  index,
}: {
  item: ConversationRetrievalItem;
  index: number;
}) {
  const TypeIcon = iconForType(item.type);
  const { emit } = useAttention("recall");
  return (
    <li className="rounded-xl border border-slate-200/60 dark:border-slate-700/50 bg-white/40 dark:bg-slate-900/30 px-3 py-2.5">
      <div className="flex items-start gap-2.5">
        <span className="mt-0.5 inline-flex w-6 h-6 items-center justify-center rounded-md bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 text-[10px] font-bold flex-shrink-0">
          {index}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-slate-600 dark:text-slate-300">
              <TypeIcon className="w-3 h-3" />
              {item.type}
            </span>
            <span className="text-[10px] text-slate-400 dark:text-slate-500">
              {item.created_at.slice(0, 10)}
            </span>
            <span className="text-[10px] text-slate-400 dark:text-slate-500">
              {item.scope}
            </span>
            {item.sources.map((s) => (
              <SourceChip key={s.store} source={s} />
            ))}
          </div>
          <p className="text-sm text-slate-700 dark:text-slate-200 leading-snug">
            {item.snippet}
          </p>
          <a
            href={`/explore?capture=${encodeURIComponent(item.id)}`}
            onClick={() =>
              void emit("open_source", {
                itemKind: item.type,
                itemId: item.id,
                context: { type: item.type, scope: item.scope },
              })
            }
            className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-orange-600 dark:text-orange-300 hover:text-orange-700 dark:hover:text-orange-200"
          >
            Open in workspace
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </li>
  );
}

function SourceChip({ source }: { source: ConversationRetrievalSource }) {
  const Icon = iconForSource(source.store);
  return (
    <span
      title={`rank ${source.rank}`}
      className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-200"
    >
      <Icon className="w-2.5 h-2.5" />
      {source.store}
    </span>
  );
}

function iconForType(type: string): LucideIcon {
  if (/note|capture/i.test(type)) return FileText;
  if (/calendar|event|meeting/i.test(type)) return Clock;
  if (/email|message|transcript/i.test(type)) return FileText;
  return Sparkles;
}

function iconForSource(store: ConversationRetrievalSource["store"]): LucideIcon {
  switch (store) {
    case "semantic":
      return Sparkles;
    case "fts":
      return Search;
    case "graph":
      return Network;
    case "temporal":
      return Clock;
  }
}
